import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GripVertical, ChevronUp, ChevronDown, Plus, Trash2, Pencil } from "lucide-react";
import { PhotoUpload } from "@/components/PhotoUpload";
import { toast } from "sonner";

import { CHECKOUT_METHODS } from "@/lib/constants";

export default function AccessStepsEditor({ apt, canEdit, onSaved }) {
  const [steps, setSteps] = useState(apt.access_steps || []);
  const [editing, setEditing] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [co, setCo] = useState({
    methods: apt.checkout_methods || [],
    kn_code: apt.checkout_keynest_code || "",
  });

  useEffect(() => { setSteps(apt.access_steps || []); }, [apt.id, apt.access_steps]);
  useEffect(() => {
    setCo({
      methods: apt.checkout_methods || [],
      kn_code: apt.checkout_keynest_code || "",
    });
  }, [apt.id, apt.checkout_methods, apt.checkout_keynest_code]);

  const saveCheckout = async (patch) => {
    try {
      await api.put(`/apartments/${apt.id}`, { ...apt, access_steps: steps, ...patch });
      toast.success("Check-out mis à jour");
      onSaved?.();
    } catch { toast.error("Erreur de sauvegarde"); }
  };

  const save = async (newSteps) => {
    const ordered = newSteps.map((s, i) => ({ ...s, order: i + 1 }));
    setSteps(ordered);
    try {
      await api.put(`/apartments/${apt.id}`, { ...apt, access_steps: ordered });
      toast.success("Étapes mises à jour");
      onSaved?.();
    } catch { toast.error("Erreur de sauvegarde"); }
  };

  const move = (from, to) => {
    if (to < 0 || to >= steps.length) return;
    const arr = [...steps];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    save(arr);
  };

  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    move(dragIdx, idx);
    setDragIdx(null);
  };

  const updateStep = (i, patch) => {
    const arr = steps.map((s, j) => (j === i ? { ...s, ...patch } : s));
    setSteps(arr);
  };

  const sorted = [...steps].sort((a, b) => a.order - b.order);

  if (!editing) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold font-display text-slate-900">Guide d'accès</h3>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-steps-btn">
              <Pencil className="w-3.5 h-3.5 mr-1.5" />Modifier
            </Button>
          )}
        </div>
        {sorted.length === 0 && <div className="text-sm text-slate-400">Aucune étape définie</div>}
        <div className="space-y-4">
          {sorted.map((step, idx) => (
            <div key={step.id} className="flex gap-3" data-testid={`access-step-${idx}`}>
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">
                  {step.order}
                </div>
                {idx < sorted.length - 1 && <div className="flex-1 w-px bg-slate-200 my-1" />}
              </div>
              <div className="flex-1 pb-2">
                <div className="font-semibold text-slate-900">{step.title}</div>
                {step.description && <div className="text-sm text-slate-600 mt-0.5">{step.description}</div>}
                {step.photo_url && (
                  <img src={step.photo_url} alt={step.title}
                       className="mt-2 rounded-lg border border-slate-200 w-full max-w-sm object-cover h-40" />
                )}
              </div>
            </div>
          ))}
        </div>
        {(apt.checkout_methods || []).length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-100" data-testid="checkout-info">
            <div className="font-semibold text-slate-900 text-sm mb-1.5">Check-out — restitution des clés</div>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {apt.checkout_methods.map((m) => (
                <span key={m} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">{m}</span>
              ))}
            </div>
            {apt.checkout_methods.includes("Boîte à clés") && (
              <div className="text-sm text-slate-600">Redéposer les clés dans la boîte à clés{apt.keybox_code ? ` (code : ${apt.keybox_code})` : ""}.</div>
            )}
            {apt.checkout_methods.includes("KeyNest") && (
              <div className="text-sm text-slate-600">
                Dépôt KeyNest au même point que le retrait{apt.keynest_address ? ` (${apt.keynest_address})` : ""}{apt.checkout_keynest_code ? ` — code dépôt : ${apt.checkout_keynest_code}` : ""}.
              </div>
            )}
            {apt.checkout_methods.includes("Réception") && (
              <div className="text-sm text-slate-600">Remettre les clés à la réception.</div>
            )}
            {apt.checkout_methods.includes("Sur place (clé sur la table)") && (
              <div className="text-sm text-slate-600">Laisser la clé sur la table dans l'appartement.</div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold font-display text-slate-900">Guide d'accès — édition</h3>
        <Button size="sm" onClick={() => { save(steps); setEditing(false); }} data-testid="save-steps-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white">Terminer</Button>
      </div>
      <div className="space-y-3">
        {sorted.map((step, idx) => (
          <div key={step.id}
               draggable
               onDragStart={() => setDragIdx(idx)}
               onDragOver={(e) => e.preventDefault()}
               onDrop={() => onDrop(idx)}
               className={`border border-slate-200 rounded-xl p-3 bg-slate-50/50 transition-all ${dragIdx === idx ? "opacity-50" : ""}`}
               data-testid={`edit-step-${idx}`}>
            <div className="flex items-start gap-2">
              <div className="flex flex-col items-center gap-0.5 pt-1 flex-shrink-0">
                <GripVertical className="w-4 h-4 text-slate-400 cursor-grab hidden sm:block" />
                <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0}
                        className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`step-up-${idx}`}>
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === sorted.length - 1}
                        className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`step-down-${idx}`}>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                  <Input value={step.title} onChange={(e) => updateStep(idx, { title: e.target.value })}
                         onBlur={() => save(steps.map((s, i) => ({ ...s, order: i + 1 })))}
                         placeholder="Titre de l'étape" data-testid={`step-title-${idx}`} className="h-8" />
                  <button type="button" onClick={() => save(steps.filter((_, j) => j !== idx))}
                          className="p-1.5 text-red-400 hover:text-red-600 flex-shrink-0" data-testid={`step-del-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Textarea value={step.description || ""} onChange={(e) => updateStep(idx, { description: e.target.value })}
                          onBlur={() => save(steps.map((s, i) => ({ ...s, order: i + 1 })))}
                          placeholder="Description" rows={2} data-testid={`step-desc-${idx}`} className="text-sm" />
                <PhotoUpload value={step.photo_url}
                             onChange={(url) => save(steps.map((s, j) => (j === idx ? { ...s, photo_url: url } : s)))}
                             testid={`step-photo-${idx}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" className="w-full mt-3"
              onClick={() => save([...steps, { id: crypto.randomUUID(), order: steps.length + 1, title: `Étape ${steps.length + 1}`, description: "", photo_url: null }])}
              data-testid="add-step-btn">
        <Plus className="w-4 h-4 mr-1.5" />Ajouter une étape
      </Button>
      <div className="mt-4 pt-3 border-t border-slate-100">
        <div className="text-sm font-medium text-slate-700 mb-1.5">Restitution des clés au check-out *</div>
        <div className="flex flex-wrap gap-2" data-testid="edit-checkout-methods">
          {CHECKOUT_METHODS.map((m) => {
            const on = co.methods.includes(m);
            return (
              <button type="button" key={m}
                      onClick={() => {
                        const methods = on ? co.methods.filter((x) => x !== m) : [...co.methods, m];
                        setCo({ ...co, methods });
                        saveCheckout({ checkout_methods: methods });
                      }}
                      data-testid={`edit-checkout-${m}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                {m}
              </button>
            );
          })}
        </div>
        {co.methods.includes("KeyNest") && (
          <div className="mt-2">
            <p className="text-[11px] text-slate-400 mb-1.5">Dépôt au même point KeyNest que le retrait{apt.keynest_address ? ` (${apt.keynest_address})` : ""}.</p>
            <Input value={co.kn_code} placeholder="Code dépôt KeyNest (8 chiffres)"
                   onChange={(e) => setCo({ ...co, kn_code: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                   inputMode="numeric" maxLength={8}
                   onBlur={() => {
                     if (co.kn_code && !/^\d{8}$/.test(co.kn_code)) { toast.error("Le code dépôt KeyNest doit contenir exactement 8 chiffres"); return; }
                     saveCheckout({ checkout_keynest_code: co.kn_code });
                   }}
                   data-testid="edit-checkout-keynest-code" className="h-8 text-sm sm:max-w-xs" />
          </div>
        )}
      </div>
    </div>
  );
}
